import React from 'react';

import styles from '../ResultCard/styles/styles.module.scss';

import AddRemoveButton from '../../shared/AddRemoveButton';
import FavoriteButton from '../../shared/FavoriteButton';
import MoveHandle from '../../shared/MoveHandle';
import CardImage from '../CardImage';
import CardImageCarousel from '../CardImageCarousel';
import CardLink from '../CardLink';
import MapTypeBadge from '../../shared/badges/MapTypeBadge';
import TrackCardContent from '../TrackCardContent';
import { getTrackProfilePhotoUrl, getTrackPhotoUrls, getFormattedTrackUrls, getTrackStaticMapUrl } from '../../../utils/track_helpers';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { shallowCompare } from '../../../utils/compare';

class TrackResultCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      photoUrls: this.getInitPhotoUrls(props.track),
      preloaded: false
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState, propKey => !propKey.includes('Click'));
  }

  getInitPhotoUrls(track) {
    let numPhotos = track.photo_count || 0;
    if (numPhotos > this.props.maxInitPhotos) {
      numPhotos = this.props.maxInitPhotos;
    }
    if (numPhotos === 0) {
      return [getTrackStaticMapUrl(track)];
    }
    const photoUrls = [getTrackStaticMapUrl(track), getTrackProfilePhotoUrl(track, 'large', false)];
    for (let i = 2; i < numPhotos; i++) {
      // Initialize React slick carousel with CardImages that don't have an image (e.g. just a background gradient)
      photoUrls.push(null);
    }
    return photoUrls;
  }

  handleMouseEnter = () => {
    // Preload image carousel photos on first hover
    if (!this.state.preloaded) {
      this.preloadPhotos();
    }
    if (this.props.messagingChannel) {
      this.props.messagingChannel.publish('item.mouseenter', this.props.track);
    }
  };

  handleMouseLeave = () => {
    if (this.props.messagingChannel) {
      this.props.messagingChannel.publish('item.mouseleave', this.props.track);
    }
  };

  handleBeforeImageChange = () => {
    // Preload image carousel photos on first change (for mobile, because mouseEnter isn't fired on mobile)
    if (!this.state.preloaded) {
      this.preloadPhotos();
    }
  };

  preloadPhotos() {
    if (!this.props.track.photo_count) {
      return;
    }
    getTrackPhotoUrls(this.props.track, this.props.maxPhotos, 'large').then(loadedUrls => {
      let { photoUrls } = this.state;
      loadedUrls = loadedUrls.slice(1); // remove profile photo
      // Combine pre-loaded static map url and profile photo url with loadedUrls (prevent image flash by keeping url the same)
      photoUrls = [photoUrls[0], photoUrls[1], ...loadedUrls];
      // hack to pre-load images by browser
      loadedUrls.forEach(photoUrl => {
        new Image().src = photoUrl;
      });
      this.setState({ preloaded: true, photoUrls });
    });
  }

  handleCardClick = e => {
    if (this.props.ignoreClicks) {
      return false;
    }
    // TODO: Move these outside of component
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'Search Page', 'Result List', `Position ${this.props.idx} Track Clicked`]);
    ServerCommunicationUtil.handleGaqPush.bind(null, ['_trackEvent', 'Search Page', 'Hovercard', 'Track Click']);
    if (this.props.handleCardClick) {
      this.props.handleCardClick();
    } else {
      const { localizedUrl } = getFormattedTrackUrls(this.props.track, this.props.context);
      window.location = localizedUrl;
    }
    return false;
  };

  renderTopLeftButton() {
    if (this.props.allowItemMoving) {
      return <MoveHandle className={styles.topLeftButton} dragHandleProps={this.props.dragHandleProps} />;
    }
    return <MapTypeBadge className={styles.badge} mapType={this.props.track.type} />;
  }

  renderTopRightButton() {
    if (this.props.allowItemDelete && this.props.handleItemDeleteClick) {
      return (
        <AddRemoveButton
          className={styles.topRightButton}
          handleClick={this.props.handleItemDeleteClick}
          isInCollection
          removeLabel={this.props.itemDeleteLabel}
        />
      );
    }
    if (this.props.showFavoriteHeart && this.props.handleFavoriteClick) {
      return (
        <FavoriteButton className={styles.topRightButton} handleFavoriteClick={this.props.handleFavoriteClick} isFavorite={this.props.isFavorite} />
      );
    }
    return null;
  }

  renderCardImage() {
    const { opaqueOverlay, track } = this.props;
    const { photoUrls } = this.state;
    if (!photoUrls) {
      return null;
    }
    if (photoUrls.length > 1 && !opaqueOverlay) {
      return (
        <CardImageCarousel
          className={styles.imageCarousel}
          classNameImage={styles.image}
          handleBeforeChange={this.handleBeforeImageChange}
          handleCardClick={this.handleCardClick}
          lowerButtons={!this.props.descriptive}
          photoUrls={photoUrls}
          title={track.name}
          slickSettings={{
            initialSlide: 1
          }}
        />
      );
    }
    return <CardImage className={styles.image} lazyload url={photoUrls[0]} title={track.name} darker={opaqueOverlay} />;
  }

  render() {
    const { localizedUrl } = getFormattedTrackUrls(this.props.track, this.props.context);
    return (
      <div className={styles.container} onMouseEnter={this.handleMouseEnter} onMouseLeave={this.handleMouseLeave}>
        {/* statically positioned */}
        <CardLink url={localizedUrl} title={this.props.track.name} handleClick={this.handleCardClick} />
        {this.renderCardImage()}
        <TrackCardContent
          bottomButtonsConfig={this.props.bottomButtonsConfig}
          className={styles.content}
          context={this.props.context}
          track={this.props.track}
        />
        {/* absolute positioned */}
        {this.renderTopLeftButton()}
        {this.renderTopRightButton()}
      </div>
    );
  }
}

TrackResultCard.defaultProps = {
  maxInitPhotos: 5, // how many to display before pre-load
  maxPhotos: 6 // how many to display after pre-load
};

export default TrackResultCard;
